/**
 * 路由级权限守卫（C1 Phase 3）。
 *
 * 直接输 URL（或点老书签）进入一个 `canView` 为 false 的页面时，渲染**明确的权限页**
 * （不是白屏、不是原始报错）——设计稿 §3.2-2。
 *
 * 三条纪律：
 *  1. **映射只有一处**：本组件只接收 `feature`，页面↔功能点的对应在
 *     `lib/permissions.ts` 的 `PAGE_FEATURES`，路由表用 `guarded()` 绑定；
 *  2. **权限数据只从会话读**：`useAuthStore` 的 `/v1/auth/me` 结果 → `accessOfAuthUser`
 *     （缺字段 = 契约 fail-closed），**不**用"有没有数据"反推；
 *  3. **会话未决时不判**：显示骨架（此刻还没有权限数据；提前判定会闪一下权限页）。
 *     未登录由 `AppShell` 统一跳登录页。
 */

import { useMemo, type ReactElement, type ReactNode } from "react";
import { accessOfAuthUser, canViewFeature } from "../lib/permissions.js";
import { useAuthStore } from "../store/authStore.js";
import { PermissionDenied } from "./PermissionDenied.js";
import { RouteSkeleton } from "./RouteSkeleton.js";

export interface FeatureGuardProps {
  readonly feature: string;
  readonly children: ReactNode;
}

export function FeatureGuard({ feature, children }: FeatureGuardProps): ReactElement {
  const status = useAuthStore((state) => state.status);
  const user = useAuthStore((state) => state.user);
  const access = useMemo(() => accessOfAuthUser(user), [user]);

  // 会话未决 / 未登录：交给 AppShell 的跳转逻辑，这里只显示骨架，绝不提前放行内容。
  if (status !== "authenticated") return <RouteSkeleton variant="cards" />;
  if (!canViewFeature(access, feature)) return <PermissionDenied featureKey={feature} />;
  return <>{children}</>;
}
